import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, FileText, Calendar, GitBranch, ChevronLeft, ChevronRight, Settings, Globe, Zap } from 'lucide-react';
import { getProject, getPrompts, createPrompt } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import AutoEvalSettings from '../components/AutoEvalSettings';

const ProjectDetail = ({ user }) => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAutoEvalSettings, setShowAutoEvalSettings] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: ''
  });

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  const loadProjectData = async () => {
    try {
      const [projectData, promptsData] = await Promise.all([
        getProject(projectId),
        getPrompts(projectId)
      ]);
      setProject(projectData);
      setPrompts(promptsData);
    } catch (error) {
      console.error('Failed to load project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.content.trim()) return;

    try {
      await createPrompt({
        projectId,
        name: formData.name,
        description: formData.description,
        content: formData.content,
        authorId: user.id,
        changeMessage: 'Initial version'
      });
      
      setFormData({ name: '', description: '', content: '' });
      setShowCreateForm(false);
      loadProjectData();
    } catch (error) {
      console.error('Failed to create prompt:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Project not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">{project.name}</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
          {project.description && (
            <p className="text-gray-600 mt-2 mb-2 mr-2">{project.description}</p>
          )}
          <div className="flex items-center text-sm text-gray-500 mt-2">
            <Calendar className="h-4 w-4 mr-1" />
            Created {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
          </div>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/projects/${projectId}/settings`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Link>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Prompt
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* Prompts List */}
        <div className="space-y-6">
          {/* Create Prompt Form */}
          {showCreateForm && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Prompt</h3>
              <form onSubmit={handleCreatePrompt} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="e.g., Email Summary Generator"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input"
                    placeholder="Brief description of what this prompt does"
                  />
                </div>
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt Content
                  </label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="textarea font-mono"
                    rows={8}
                    placeholder="You are a helpful assistant that summarizes emails in a friendly tone..."
                    required
                  />
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="btn-primary">
                    Create Prompt
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Prompts List */}
          {prompts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No prompts yet</h3>
              <p className="text-gray-600 mb-4">Create your first prompt to start version tracking</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Prompt
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {prompts.map((prompt) => (
                <Link
                  key={prompt.id}
                  to={`/prompts/${prompt.id}`}
                  className="card p-6 hover:shadow-lg transition-shadow duration-200 group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {prompt.name}
                      </h3>
                      {prompt.description && (
                        <p className="text-gray-600 text-sm mt-1">{prompt.description}</p>
                      )}
                      <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                        <div className="flex items-center">
                          <GitBranch className="h-4 w-4 mr-1" />
                          {prompt.version_count} version{prompt.version_count !== '1' ? 's' : ''}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Last updated {formatDistanceToNow(new Date(prompt.last_updated || prompt.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Auto-evaluation Settings Modal */}
      {showAutoEvalSettings && (
        <AutoEvalSettings
          projectId={projectId}
          onClose={() => setShowAutoEvalSettings(false)}
        />
      )}
    </div>
  );
};

export default ProjectDetail; 